/**
 * Session keepalive heartbeat module.
 *
 * Sends periodic heartbeat pings to /api/session/heartbeat when the user is
 * actively viewing the page. The heartbeat triggers the backend's existing
 * post-invocation cookie refresh, preventing idle session timeout.
 *
 * Activity is tracked via mousemove, click, keydown, scroll, and touchstart
 * events on document. The heartbeat pauses when the tab is hidden (Visibility API).
 */

const HEARTBEAT_URL = "/api/session/heartbeat";
const HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes, matches IdleRefreshInterval

const ACTIVITY_EVENTS: ReadonlyArray<string> = [
  "mousemove",
  "click",
  "keydown",
  "scroll",
  "touchstart",
];

let userWasActive = false;
let intervalId: ReturnType<typeof setInterval> | null = null;
let hiddenAt: number | null = null;

function onActivity(): void {
  userWasActive = true;
}

function redirectToLanding(): void {
  window.location.href = "/?session_expired=1";
}

async function sendHeartbeat(): Promise<void> {
  if (!userWasActive) {
    return;
  }

  try {
    const response = await fetch(HEARTBEAT_URL);
    if (response.status === 401) {
      userWasActive = false;
      stopSessionKeepalive();
      redirectToLanding();
      return;
    }
    if (response.ok) {
      userWasActive = false;
    }
  } catch {
    console.warn("Session heartbeat failed (network error)");
  }
}

function addActivityListeners(): void {
  for (const event of ACTIVITY_EVENTS) {
    document.addEventListener(event, onActivity, { passive: true });
  }
}

function removeActivityListeners(): void {
  for (const event of ACTIVITY_EVENTS) {
    document.removeEventListener(event, onActivity);
  }
}

function onVisibilityChange(): void {
  if (document.visibilityState === "hidden") {
    // Pause: stop interval and listeners
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
    removeActivityListeners();
    hiddenAt = Date.now();
  } else {
    // Resume: re-add listeners and restart interval
    addActivityListeners();
    intervalId = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    // If user was active before hiding AND enough time elapsed, send immediate ping
    if (
      userWasActive &&
      hiddenAt !== null &&
      Date.now() - hiddenAt >= HEARTBEAT_INTERVAL_MS
    ) {
      sendHeartbeat();
    }
    hiddenAt = null;
  }
}

export function startSessionKeepalive(): void {
  if (intervalId !== null) return;

  userWasActive = false;
  hiddenAt = null;
  addActivityListeners();
  intervalId = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
  document.addEventListener("visibilitychange", onVisibilityChange);
}

export function stopSessionKeepalive(): void {
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
  removeActivityListeners();
  document.removeEventListener("visibilitychange", onVisibilityChange);
  userWasActive = false;
  hiddenAt = null;
}
